/**
 * Pure display helpers for internal reports, shared by the backend renderer (email
 * body, PDF, CSV) and the in-app run modal so every report — including future ones —
 * formats links and dates the same way. No I/O, no framework dependencies.
 */
/** Reports display times in Pacific Time (PDT/PST switches automatically). */
export const REPORT_TIME_ZONE = 'America/Los_Angeles';
const DATE_ONLY_FORMAT = new Intl.DateTimeFormat('en-US', {
    timeZone: 'UTC',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
});
const DATE_TIME_FORMAT = new Intl.DateTimeFormat('en-US', {
    timeZone: REPORT_TIME_ZONE,
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZoneName: 'short',
});
/**
 * Formats a report date cell for humans: `Jul 15, 2026` for date-only values,
 * `Jul 15, 2026, 3:42 PM PDT` for timestamps (Pacific, am/pm).
 *
 * SQL DATE columns arrive as UTC-midnight instants; converting those to Pacific would
 * show the PREVIOUS day, so exactly-midnight-UTC values render as the date alone (in
 * UTC). Unparseable values pass through unchanged.
 */
export function formatReportDateValue(raw) {
    const trimmed = raw.trim();
    if (!trimmed)
        return raw;
    const parsed = new Date(trimmed);
    if (Number.isNaN(parsed.getTime()))
        return raw;
    const dateOnly = parsed.getUTCHours() === 0 &&
        parsed.getUTCMinutes() === 0 &&
        parsed.getUTCSeconds() === 0 &&
        parsed.getUTCMilliseconds() === 0;
    const formatted = dateOnly ? DATE_ONLY_FORMAT.format(parsed) : DATE_TIME_FORMAT.format(parsed);
    // Newer ICU emits narrow no-break spaces before AM/PM; normalize for plain display.
    return formatted.replace(/[\u202f\u00a0]/g, ' ');
}
/** Matches a cell that is exactly one absolute http/https URL. */
const ABSOLUTE_URL_RE = /^https?:\/\/\S+$/i;
/** Matches a cell that is exactly one in-app path, e.g. `/projects/<id>`. */
const APP_PATH_RE = /^\/[A-Za-z0-9][^\s]*$/;
/**
 * Resolves a report cell that IS a link (rather than merely containing one) to a
 * clickable href: absolute http(s) URLs pass through, `www.` gets a scheme, and in-app
 * paths like `/projects/<id>` are prefixed with `baseUrl` (empty base = relative link,
 * which is what the in-app viewer wants). Returns null for ordinary text.
 */
export function reportCellHref(raw, baseUrl = '') {
    const trimmed = raw.trim();
    if (!trimmed)
        return null;
    if (ABSOLUTE_URL_RE.test(trimmed))
        return trimmed;
    if (/^www\.\S+$/i.test(trimmed))
        return `https://${trimmed}`;
    if (APP_PATH_RE.test(trimmed))
        return `${baseUrl.replace(/\/$/, '')}${trimmed}`;
    return null;
}
//# sourceMappingURL=report-format.js.map